import { useState, useEffect } from 'react';
import { getRequest } from 'utils/helper';

interface DagHistoryItem {
  dagrunId: string;
  ecid: string;
  dagrunStatus: string;
  dagrunStartedAt: string;
  dagrunCompletedAt: string;
  errorMessage: string;
  message: string;
}

interface UseDagHistoryOptions {
  companyKey?: string | null;
  connectorIdentifier?: string | null;
  enabled?: boolean;
  dagHistorySaved?: boolean;
}

interface UseDagHistoryReturn {
  dagHistory: DagHistoryItem[];
  isLoading: boolean;
  error: Error | null;
  hasSuccessfulRun: boolean;
}

export const useDagHistory = (
  options: UseDagHistoryOptions
): UseDagHistoryReturn => {
  const {
    companyKey,
    connectorIdentifier,
    enabled = true,
    dagHistorySaved
  } = options;

  const [dagHistory, setDagHistory] = useState<DagHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchDagrunHistory = async () => {
      if (!enabled || !companyKey || !connectorIdentifier) {
        setDagHistory([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      const queryParams = {
        companyKey: companyKey,
        connectorIdentifier: connectorIdentifier
      };

      try {
        const res = await getRequest(
          '/integration-settings-api/dagrun-history',
          queryParams,
          true
        );

        if (res.data && Array.isArray(res.data)) {
          setDagHistory(res.data);
        } else {
          setDagHistory([]);
        }
      } catch (err: any) {
        console.error('Error fetching DAG history:', err);
        setError(err);
        setDagHistory([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDagrunHistory();
  }, [companyKey, connectorIdentifier, enabled, dagHistorySaved]);

  const hasSuccessfullRun = dagHistory.some(
    (run) => run.dagrunStatus === 'success'
  );

  return {
    dagHistory,
    isLoading,
    error,
    hasSuccessfulRun: hasSuccessfullRun
  };
};
